<!--
  Harvesting 2.0 — Dashboard.

  L'ÉCRAN SUIT LE MAIL DU MATIN (Chady, 2026-10-27 : « on a beaucoup travaillé
  le mail, pas la page »). Mêmes blocs, même ordre, mêmes chiffres : ils
  viennent du service du rapport (dashboardFigures), l'écran ne fait que les
  rédiger en français.
    1. l'état des deux synchros, sur une ligne ;
    2. les cumuls de l'année (l'en-tête du mail) ;
    3. les quatre tuiles du mail : urgences, harvesting et achat de la veille,
       APV à encoder ;
    4. Urgences, le tableau du mail — plus les chargements sous le seuil, que
       le mail ne porte plus ;
    5. les corrections de volume SUMAL ;
    6. les avize de la veille ;
    7. les APV à encoder, avec le bouton « Encode » du mail ;
    8. la semaine ;
  puis ce que le mail n'a pas : les grandeurs, le rythme, « comment c'est
  calculé ».

  La règle de la spec reste appliquée à la lettre : l'échéance prime toujours
  sur le volume. Cinq mètres cubes à sortir demain passent devant six cents dans
  deux mois — le volume n'ordonne qu'à l'intérieur d'un même niveau d'urgence
  (urgentRows, côté serveur).
-->
<template>
  <div>
    <!-- =========================== 1. SYNCHROS =========================== -->
    <div class="hv2-strip">
      <span class="hv2-strip-item" :class="syncX2.tone ? `is-${syncX2.tone}` : ''">
        <span class="hv2-strip-dot"></span>
        Fiches dépôt SUMAL : <b>{{ syncX2.label }}</b>
        <span v-if="syncX2.detail" class="ml-1">· {{ syncX2.detail }}</span>
      </span>
      <span class="hv2-strip-item" :class="syncX1.tone ? `is-${syncX1.tone}` : ''">
        <span class="hv2-strip-dot"></span>
        Avize : <b>{{ syncX1.label }}</b>
        <span v-if="syncX1.detail" class="ml-1">· {{ syncX1.detail }}</span>
      </span>
      <span class="hv2-strip-item">
        Chantiers actifs : <b>{{ health.active_sites }}</b> sur {{ parcels.length }} APV suivis
      </span>
    </div>

    <!-- ===================== 2. LES CUMULS DE L'ANNÉE ===================== -->
    <!-- L'en-tête du mail : deux métiers, deux chiffres, jamais additionnés. -->
    <div v-if="daily && daily.year" class="hv2-stats mb-base">
      <div class="vx-col w-full sm:w-1/2">
        <hv-stat tier="tile" :value="m3int(daily.year.harvesting_m3)" unit="m³"
                 :label="`transportés par XYLO depuis le 1er janvier ${daily.year.to.slice(0, 4)}`"
                 :sub="splitText(daily.year.harvesting_split)" />
      </div>
      <div class="vx-col w-full sm:w-1/2">
        <hv-stat tier="tile" :value="m3int(daily.year.achat_m3)" unit="m³"
                 label="achetés à d'autres exploitants depuis le 1er janvier"
                 :sub="splitText(daily.year.achat_split)" />
      </div>
    </div>

    <!-- ======================= 3. LES TUILES DU MAIL ======================= -->
    <!-- La densité vit DANS la tuile de son métier, comme dans le mail. Le
         lundi, la fenêtre couvre le week-end, exactement comme le rapport. -->
    <div class="hv2-stats mb-base">
      <div class="vx-col w-1/2 lg:w-1/4">
        <hv-stat tier="tile" :value="int(urgent.length)" unit="APV" label="urgences"
                 :tone="urgent.length ? 'danger' : ''" :sub="urgentBreakdown" />
      </div>
      <div v-if="daily" class="vx-col w-1/2 lg:w-1/4">
        <hv-stat tier="tile" :value="m3(daily.harvesting.m3)" unit="m³"
                 :label="daily.window.weekend ? 'Harvesting week-end' : 'Harvesting hier'"
                 :sub="harvestingSub" />
      </div>
      <div v-if="daily" class="vx-col w-1/2 lg:w-1/4">
        <hv-stat tier="tile" :value="m3(daily.achat.m3)" unit="m³"
                 :label="daily.window.weekend ? 'Achat week-end' : 'Achat hier'"
                 :tone="daily.achat.absent ? 'danger' : ''"
                 :sub="achatSub" />
      </div>
      <div class="vx-col w-1/2 lg:w-1/4">
        <hv-stat tier="tile" :value="int(toEncode.length)" label="APV à encoder"
                 :tone="toEncodeOut.length ? 'danger' : (toEncode.length ? 'warn' : '')"
                 :sub="toEncodeSub">
          <div v-if="toEncode.length" class="mt-2"><hv-encode-button /></div>
        </hv-stat>
      </div>
    </div>

    <!-- Le périmètre du tableau de bord, écrit. Tous les chiffres portent sur
         les seules parcelles de l'horizon : ne pas le dire les rendrait faux
         pour qui compare avec l'ancienne version. -->
    <p v-if="horizon && (horizon.hidden || horizon.dismissed)" class="hv2-sec-sub mb-base">
      Ces chiffres portent sur {{ horizon.shown }} APV.
      <span v-if="horizon.hidden">
        {{ horizon.hidden }} sans aucune activité depuis le {{ date(horizon.from) }} sont hors périmètre.
      </span>
      <span v-if="horizon.archived">
        {{ horizon.archived }} ont leur dépôt fermé depuis plus de
        {{ horizon.closed_archive_days || 90 }} jours.
      </span>
      <span v-if="horizon.dismissed">{{ horizon.dismissed }} ont été écartés à la main.</span>
    </p>

    <!-- ============================ 4. URGENCES ============================ -->
    <!-- Les lignes de la section 1 du mail (urgentRows) : depuis le 29/10/2026,
         tous les APV ouverts (ni repris par l'ocol, ni clos), l'échéance d'abord ;
         `alert` marque ceux qui sont réellement en alerte. -->
    <vx-card class="mb-base">
      <hv-section title="Urgences" :sub="URGENT_SUB">
        <template slot="actions">
          <hv-pill v-if="urgent.length" tone="brand" :label="`${urgent.length} APV`" />
          <hv-pill v-if="urgentAlerting" tone="danger" icon="AlertCircleIcon" :label="`${urgentAlerting} en alerte`" />
          <hv-pill v-else-if="!loading && !error && !urgentError" tone="ok" icon="CheckCircleIcon" label="Rien d'urgent" />
        </template>

        <hv-table :rows="urgent" row-key="apv" :loading="loading" :error="error || urgentError"
                  pagination :max-items="15" empty-icon="CoffeeIcon"
                  empty-text="Aucun APV ouvert.">
          <template slot="thead">
            <vs-th>APV</vs-th><vs-th>Dépôt SUMAL</vs-th><vs-th>Échéance</vs-th>
            <vs-th class="hv2-col-n">Stock restant</vs-th><vs-th class="hv2-col-n">APV − sorti − stock</vs-th>
            <vs-th class="hv2-col-n">Densité kg/m³</vs-th><vs-th>Motif</vs-th>
          </template>
          <template slot="row" slot-scope="{ r }">
            <vs-td><hv-apv-cell :code="r.apv" :label="r.label" :p="parcelOf(r)" /></vs-td>
            <vs-td><hv-depot-cell :p="parcelOf(r) || r.parcelle" :ocol="ocols[r.apv]" /></vs-td>
            <vs-td class="hv2-nowrap"><hv-due-cell :days="r.days" :closed-days="r.closedDays" /></vs-td>
            <vs-td class="hv2-col-n">
              <template v-if="Number(r.m3) >= rules.min_m3">
                <b>{{ m3(r.m3) }}</b>
                <div v-if="r.fromStock" class="hv2-cell-line">stock SUMAL</div>
              </template>
              <span v-else class="hv2-muted">—</span>
            </vs-td>
            <vs-td class="hv2-col-n"><hv-gap-cell :p="parcelOf(r)" :pct="rules.done_residual_pct" /></vs-td>
            <vs-td class="hv2-col-n">
              <hv-density-cell :p="parcelOf(r)" :min="rules.density_min" :max="rules.density_max" />
            </vs-td>
            <vs-td>
              <!-- L'autorisation d'abord : c'est elle qui fait le retard. La
                   désființare à venir passe en seconde pastille. -->
              <template v-if="r.expiredGap">
                <hv-pill tone="danger" :label="`Autorisation expirée depuis le ${date(r.permitExpiry)}`" />
                <div v-if="r.gap !== null" class="hv2-cell-line">{{ m3(r.gap) }} m³ ni sortis, ni au dépôt</div>
                <div v-if="r.extended" class="mt-1"><hv-pill :label="`Dépôt jusqu'au ${date(r.deadline)}`" /></div>
                <div v-if="r.paceShort" class="mt-1"><hv-pill tone="warn" label="Rythme trop lent" /></div>
                <div v-if="r.paceShort && r.paceText" class="hv2-cell-line">{{ r.paceText }}</div>
              </template>
              <template v-else>
                <hv-pill v-if="motif(r)" :label="motif(r).label" :tone="motif(r).tone" />
                <div v-if="r.pace" :class="{ 'mt-1': !!motif(r) }"><hv-pill tone="warn" label="Rythme trop lent" /></div>
                <div v-if="r.pace && r.paceText" class="hv2-cell-line">{{ r.paceText }}</div>
              </template>
              <div v-if="needsEncode(r)" class="mt-2"><hv-encode-button :apv="r.apv" /></div>
            </vs-td>
          </template>
        </hv-table>

        <p v-if="urgentHidden" class="hv2-sec-sub mt-3">
          {{ urgentHidden === 1
            ? 'Un APV repris par l\'ocol ou clos n\'apparaît plus ici.'
            : `${urgentHidden} APV repris par l'ocol ou clos n'apparaissent plus ici.` }}
        </p>

        <!-- Les chargements sous le seuil ont quitté le mail avec leur section ;
             ils restent ici, à part : ce n'est pas une échéance. -->
        <div v-if="lowLoading.length" class="mt-6">
          <div class="hv2-subhead">Chargements sous le seuil (tableau de bord seulement)</div>
          <div v-for="r in lowLoading" :key="`ll-${r.apv_code}`" class="hv2-queue-item is-warn">
            <div class="hv2-queue-main">
              <div class="hv2-queue-title">
                <router-link :to="parcelLink(r)">{{ r.apv_code }}</router-link> — {{ parcelName(r) }}
              </div>
              <div class="hv2-queue-why">
                {{ num(r.m3_per_run, 1) }} m³ par course sur {{ r.runs }} courses — sous le seuil de
                {{ (alerts.thresholds || {}).min_m3_per_run || 20 }}. Le transport à la course devient cher.
              </div>
            </div>
          </div>
        </div>
      </hv-section>
    </vx-card>

    <!-- ===================== 5. CORRECTIONS SUMAL ===================== -->
    <!-- Toujours présent, comme dans le mail : un bloc qui disparaît se lit
         comme une synchro en panne. -->
    <vx-card v-if="daily && daily.adjustments" class="mb-base">
      <hv-section :title="`Corrections de volume APV dans SUMAL (${rules.adjust_days} derniers jours)`" :sub="ADJUST_SUB">
        <template slot="actions">
          <hv-pill v-if="daily.adjustments.length"
                   :label="`${daily.adjustments.length} opération${daily.adjustments.length > 1 ? 's' : ''}`" />
          <hv-pill v-else tone="ok" icon="CheckCircleIcon" label="Aucune" />
        </template>
        <hv-table :rows="daily.adjustments" row-key="id_rulaj" pagination :max-items="15"
                  empty-icon="CheckCircleIcon"
                  :empty-text="`Aucune correction de volume dans le registre SUMAL ces ${rules.adjust_days} derniers jours.`">
          <template slot="thead">
            <vs-th>Date PV</vs-th><vs-th>APV</vs-th><vs-th>Opération SUMAL</vs-th>
            <vs-th class="hv2-col-n">m³ pour l'APV</vs-th><vs-th class="hv2-col-n">% de l'APV</vs-th>
            <vs-th class="hv2-col-n">Densité kg/m³</vs-th><vs-th>Statut</vs-th>
          </template>
          <template slot="row" slot-scope="{ r }">
            <vs-td class="hv2-nowrap">
              {{ date(r.pv_day) }}
              <div v-if="r.is_new" class="mt-1"><hv-pill tone="info" label="nouveau dans l'ERP" /></div>
            </vs-td>
            <vs-td>
              <hv-apv-cell :code="r.apv_code" :label="depotLabel(r)" :p="parcelByApv.get(r.apv_code) || null" :details="false">
                <div v-if="ocols[r.apv_code] && ocols[r.apv_code].ocol" class="hv2-cell-line">
                  Ocol : <b>{{ ocols[r.apv_code].ocol }}</b>
                </div>
                <div v-if="ocols[r.apv_code] && ocols[r.apv_code].vendeur" class="hv2-cell-line">
                  Vendeur : <b>{{ ocols[r.apv_code].vendeur }}</b>
                </div>
              </hv-apv-cell>
            </vs-td>
            <vs-td>
              {{ opLabel(r) }}
              <div v-if="r.numar_pv" class="hv2-cell-line">PV {{ r.numar_pv }}</div>
            </vs-td>
            <vs-td class="hv2-col-n">
              <span :class="{ 'hv2-strike': cancelled(r) }">
                <span v-if="Math.abs(Number(r.volume_m3) || 0) < 0.05" class="hv2-muted">0,0</span>
                <b v-else :class="{ 'hv2-text-danger': Number(r.volume_m3) < 0 }">{{ signed(r.volume_m3) }}</b>
              </span>
            </vs-td>
            <vs-td class="hv2-col-n">
              <template v-if="!cancelled(r) && adjustShare(r)">
                <b>{{ adjustShare(r).pct }}</b>
                <div class="hv2-cell-line">de {{ m3int(adjustShare(r).contract) }} m³ contrat</div>
              </template>
              <span v-else class="hv2-muted">{{ DASH }}</span>
            </vs-td>
            <vs-td class="hv2-col-n">
              <hv-density-cell :p="parcelByApv.get(r.apv_code) || null" :min="rules.density_min" :max="rules.density_max" />
            </vs-td>
            <vs-td>
              <hv-pill v-if="cancelled(r)" tone="warn" :label="String(r.status).replace('_', ' ')" />
              <hv-pill v-else tone="ok" label="enregistré" />
            </vs-td>
          </template>
        </hv-table>
      </hv-section>
    </vx-card>

    <!-- ============ 6. LES AVIZE D'HIER : la section 2 du mail ============ -->
    <!-- Les mêmes lignes que le mail, servies par le même service
         (dailyFigures.transports) : ordre, densité et écarts ne sont pas
         recalculés ici. La plaque est celle du camion déclaré sur l'aviz ;
         aucune remorque n'est renseignée en base, rien à afficher pour elle. -->
    <vx-card v-if="daily && daily.transports" class="mb-base">
      <hv-section
        :title="daily.window.weekend ? 'Les avize du week-end' : 'Les avize d\'hier'"
        :sub="avizeSub">
        <div class="hv2-subhead">Harvesting : avize de parcelle APV</div>
        <hv-table :rows="daily.transports.harvesting" row-key="idoperation" pagination :max-items="20"
                  empty-icon="TruckIcon"
                  :empty-text="daily.window.weekend ? 'Aucun aviz de parcelle ce week-end.' : 'Aucun aviz de parcelle hier.'">
          <template slot="thead">
            <vs-th>APV</vs-th><vs-th>Aviz</vs-th><vs-th>Plaque</vs-th>
            <vs-th class="hv2-col-n">m³</vs-th><vs-th class="hv2-col-n">Tonnage t</vs-th><vs-th class="hv2-col-n">kg/m³</vs-th>
          </template>
          <template slot="row" slot-scope="{ r }">
            <vs-td>
              <router-link :to="parcelLink(r)">{{ r.apv_code }}</router-link>
              <div class="hv2-muted">{{ apvName(r) }}</div>
            </vs-td>
            <vs-td>
              <span class="hv2-code">{{ r.aviz || DASH }}</span>
              <div v-if="r.emitted" class="hv2-muted">{{ r.emitted }}</div>
            </vs-td>
            <vs-td class="hv2-code hv2-nowrap">{{ r.plate || DASH }}</vs-td>
            <vs-td class="hv2-col-n">{{ m3(r.m3) }}</vs-td>
            <vs-td class="hv2-col-n">
              {{ tonnes(r.tonnes) }}
              <div v-if="r.weighing_avize > 1" class="hv2-muted">pesée commune à {{ r.weighing_avize }} avize</div>
            </vs-td>
            <vs-td class="hv2-col-n">
              <hv-pill v-if="r.density_suspect" tone="warn" :label="`${int(r.density)} · à vérifier`" />
              <b v-else-if="r.density !== null">{{ int(r.density) }}</b>
              <span v-else class="hv2-muted">{{ DASH }}</span>
            </vs-td>
          </template>
        </hv-table>

        <div v-if="daily.transports.harvesting_absent && daily.transports.harvesting_absent.length"
             class="hv2-error mt-3">
          <b>{{ daily.transports.harvesting_absent.length }} transport(s) harvesting reçu(s) dans SUMAL
            mais absent(s) de l'ERP :</b>
          <span v-for="h in daily.transports.harvesting_absent" :key="h.aviz">
            {{ h.aviz }} ({{ m3(h.m3) }} m³)
          </span>
        </div>

        <div class="mt-6">
          <div class="hv2-subhead">Achat : avize de réception</div>
          <hv-table :rows="daily.transports.achat" row-key="idoperation" pagination :max-items="20"
                    empty-icon="TruckIcon"
                    :empty-text="daily.window.weekend ? 'Aucun aviz d\'achat ce week-end.' : 'Aucun aviz d\'achat hier.'">
            <template slot="thead">
              <vs-th>Aviz</vs-th><vs-th>Plaque</vs-th><vs-th>Fournisseur</vs-th>
              <vs-th class="hv2-col-n">m³</vs-th><vs-th class="hv2-col-n">Tonnage t</vs-th><vs-th class="hv2-col-n">kg/m³</vs-th>
            </template>
            <template slot="row" slot-scope="{ r }">
              <vs-td>
                <span class="hv2-code">{{ r.aviz || DASH }}</span>
                <div v-if="r.emitted" class="hv2-muted">{{ r.emitted }}</div>
                <!-- Entré au dépôt dans SUMAL, aucune opération ERP : l'import
                     SumalX1 l'a manqué (aviz FSC notamment). Réception à créer. -->
                <div v-if="r.absent_erp" class="mt-1">
                  <hv-pill tone="danger" label="Absent de l'ERP"
                           title="Reçu dans SUMAL, aucune opération dans XYLO : réception à créer." />
                </div>
              </vs-td>
              <vs-td class="hv2-code hv2-nowrap">{{ r.plate || DASH }}</vs-td>
              <vs-td>{{ r.fournisseur || DASH }}</vs-td>
              <vs-td class="hv2-col-n">{{ m3(r.m3) }}</vs-td>
              <vs-td class="hv2-col-n">
                {{ tonnes(r.tonnes) }}
                <div v-if="r.weighing_avize > 1" class="hv2-muted">pesée commune à {{ r.weighing_avize }} avize</div>
              </vs-td>
              <vs-td class="hv2-col-n">
                <hv-pill v-if="r.density_suspect" tone="warn" :label="`${int(r.density)} · à vérifier`" />
                <b v-else-if="r.density !== null">{{ int(r.density) }}</b>
                <span v-else class="hv2-muted">{{ DASH }}</span>
              </vs-td>
            </template>
          </hv-table>
        </div>
      </hv-section>
    </vx-card>

    <!-- ========================= 7. APV À ENCODER ========================= -->
    <!-- La section 3 du mail, avec son bouton : « Encode » ouvre /apv-encode,
         où Claude lit le contrat et prépare la réception (Chady, 2026-10-27).
         Même liste, même ordre : le bois déjà sorti d'abord, puis l'échéance. -->
    <vx-card class="mb-base">
      <hv-section title="APV à encoder" :sub="ENCODE_SUB">
        <template slot="actions">
          <span v-if="toEncode.length" class="hv2-pills">
            <hv-pill tone="warn" :label="`${toEncode.length} APV`" />
            <hv-encode-button />
          </span>
          <hv-pill v-else-if="!loading && !error" tone="ok" icon="CheckCircleIcon" label="Aucun" />
        </template>

        <div v-if="toEncodeOut.length" class="hv2-error mb-3">
          <b>{{ toEncodeOut.length }} APV</b> {{ toEncodeOut.length > 1 ? 'ont' : 'a' }} déjà
          <b>{{ m3(toEncodeOutM3) }} m³</b> sortis de forêt sans aucune réception d'achat.
        </div>

        <hv-table :rows="toEncode" :loading="loading" :error="error" pagination :max-items="15"
                  empty-icon="CheckCircleIcon" empty-text="Aucun : chaque APV a sa réception d'achat.">
          <template slot="thead">
            <vs-th>APV</vs-th><vs-th>Dépôt SUMAL</vs-th><vs-th class="hv2-col-n">Avize</vs-th>
            <vs-th class="hv2-col-n">m³ sortis</vs-th><vs-th class="hv2-col-n">Stock SUMAL m³</vs-th>
            <vs-th>Échéance</vs-th><vs-th></vs-th>
          </template>
          <template slot="row" slot-scope="{ r }">
            <vs-td>
              <hv-apv-cell :code="r.apv_code" :p="r">
                <div v-if="r.source === 'sumal'" class="mt-1"><hv-pill tone="info" label="SUMAL seul" /></div>
              </hv-apv-cell>
            </vs-td>
            <vs-td><hv-depot-cell :p="r" :ocol="ocols[r.apv_code]" /></vs-td>
            <vs-td class="hv2-col-n">{{ int(r.avize) }}</vs-td>
            <vs-td class="hv2-col-n">
              <b v-if="Number(r.m3_out) > 0" class="hv2-text-danger">{{ m3(r.m3_out) }}</b>
              <span v-else class="hv2-muted">{{ DASH }}</span>
            </vs-td>
            <vs-td class="hv2-col-n">
              <b v-if="Number(r.sumal_stock_m3) >= 0.05">{{ m3(r.sumal_stock_m3) }}</b>
              <span v-else class="hv2-muted">{{ DASH }}</span>
            </vs-td>
            <vs-td class="hv2-nowrap"><hv-due-cell :days="r.days_to_expiry" :closed-days="r.depot_closed_days" /></vs-td>
            <vs-td><hv-encode-button :apv="r.apv_code" /></vs-td>
          </template>
        </hv-table>
      </hv-section>
    </vx-card>

    <!-- =========================== 8. LA SEMAINE =========================== -->
    <vx-card v-if="daily && daily.week" class="mb-base">
      <hv-section title="La semaine" :sub="weekSub">
        <template slot="actions">
          <hv-pill :label="`${date(daily.week.window.from).slice(0, 5)} → ${date(daily.week.window.to).slice(0, 5)}`" />
        </template>
        <p v-if="!week.avize" class="hv2-sec-sub">
          Aucun aviz cette semaine. {{ int(daily.week.receptions) }} réception(s) d'achat créée(s).
        </p>
        <div v-else class="hv2-stats">
          <div class="vx-col w-1/2 md:w-1/3 lg:w-1/5">
            <hv-stat tier="mini" :value="int(week.avize)" label="avize" :sub="`${int(week.apv)} parcelle(s)`" />
          </div>
          <div class="vx-col w-1/2 md:w-1/3 lg:w-1/5">
            <hv-stat tier="mini" :value="m3int(week.m3)" unit="m³" label="sortis de forêt" />
          </div>
          <div class="vx-col w-1/2 md:w-1/3 lg:w-1/5">
            <hv-stat tier="mini" :value="int(week.trucks.total)" label="camions"
                     :sub="week.trucks.estimated ? `dont ${int(week.trucks.estimated)} estimé(s)` : 'tous pesés'" />
          </div>
          <div class="vx-col w-1/2 md:w-1/3 lg:w-1/5">
            <hv-stat tier="mini" :value="week.density !== null ? int(week.density) : DASH"
                     :unit="week.density !== null ? 'kg/m³' : ''" label="densité"
                     :sub="`${int(week.weighed)} pesé(s) sur ${int(week.avize)}`" />
          </div>
          <div class="vx-col w-1/2 md:w-1/3 lg:w-1/5">
            <hv-stat tier="mini" :value="int(daily.week.receptions)" label="réceptions" sub="d'achat créées" />
          </div>
        </div>
        <p v-if="week.suspect" class="hv2-sec-sub mt-2">
          {{ int(week.suspect) }} pesée(s) hors de l'intervalle {{ int(rules.density_min) }}–{{ int(rules.density_max) }} kg/m³,
          écartée(s) de la densité moyenne.
        </p>
      </hv-section>
    </vx-card>

    <!-- ================= 9. LES GRANDEURS (hors du mail) ================= -->
    <div class="hv2-stats mb-base">
      <div class="vx-col w-full lg:w-1/4">
        <hv-stat
          tier="hero"
          :value="m3int(kpi.remaining)"
          unit="m³"
          label="restent à sortir sur permis actif"
          :sub="`Sur ${kpi.liveParcels} chantier(s) dont l'autorisation court encore. C'est l'argent immobilisé qui a une date.`" />
      </div>
      <div class="vx-col w-full sm:w-1/2 lg:w-1/4">
        <hv-stat
          tier="tile"
          :value="m3int(kpi.out12m)"
          unit="m³"
          label="sortis sur 12 mois"
          :sub="`${int(kpi.avize12m)} avize`" />
      </div>
      <div class="vx-col w-full sm:w-1/2 lg:w-1/4">
        <hv-stat
          tier="tile"
          :value="m3int(counters.not_yet_contracted_m3)"
          unit="m³"
          label="sortis sans réception"
          :tone="counters.not_yet_contracted_parcels ? 'danger' : ''"
          :sub="counters.not_yet_contracted_parcels
            ? `${counters.not_yet_contracted_parcels} APV sans prix, sans garantie, sans échéancier`
            : 'Tous les APV ont leur facture d\'achat'" />
      </div>
      <div class="vx-col w-full sm:w-1/2 lg:w-1/4">
        <hv-stat tier="tile" :value="m3int(kpi.sumalStock)" unit="m³" label="stock déclaré aux dépôts" />
      </div>
    </div>

    <div class="hv2-stats mb-base">
      <div class="vx-col w-1/3">
        <hv-stat tier="mini" :value="int(kpi.activeParcels)" label="APV en cours" />
      </div>
      <div class="vx-col w-1/3">
        <hv-stat tier="mini" :value="pct(kpi.extractionAvg)" label="taux de sortie moyen" />
      </div>
      <div class="vx-col w-1/3">
        <hv-stat tier="mini" :value="int(kpi.expiring30)" label="échéances sous 30 j, chantier ouvert" />
      </div>
    </div>

    <!-- ======================== 10. LE RYTHME ======================== -->
    <div class="vx-row mb-base">
      <div class="vx-col w-full lg:w-1/2 mb-base lg:mb-0">
        <vx-card>
          <hv-section
            title="Le rythme de sortie"
            :sub="`Mètres cubes sortis par semaine sur ${pace.weeks} semaines. La ligne pointillée est la moyenne de la période observée.`">
            <div v-if="paceSeries.length">
              <vue-apex-charts type="area" height="260" :options="paceChart.options" :series="paceChart.series" />
            </div>
            <hv-empty v-else icon="BarChart2Icon" text="Aucun aviz sur la période." />
          </hv-section>
        </vx-card>
      </div>

      <div class="vx-col w-full lg:w-1/2">
        <vx-card>
          <hv-section
            title="Ce qui reste, et le temps pour le sortir"
            sub="Chaque barre est un chantier ouvert. La longueur est le volume restant, la couleur le temps qu'il reste avant l'échéance.">
            <div v-if="remainingChart.series[0].data.length">
              <vue-apex-charts type="bar" height="260" :options="remainingChart.options" :series="remainingChart.series" />
            </div>
            <hv-empty v-else icon="CheckCircleIcon" text="Aucun chantier ouvert avec du volume restant." />
          </hv-section>
        </vx-card>
      </div>
    </div>

    <!-- ================= 11. COMMENT C'EST CALCULÉ ================= -->
    <vx-card>
      <div class="hv2-disc" style="border-top:0; padding-top:0; margin-top:0">
        <div class="hv2-disc-head" role="button" tabindex="0"
             @click="showDisc = !showDisc" @keyup.enter="showDisc = !showDisc">
          <feather-icon :icon="showDisc ? 'ChevronUpIcon' : 'ChevronDownIcon'" svgClasses="w-4 h-4" />
          Comment c'est calculé, et ce que ça ne voit pas
        </div>
        <div v-if="showDisc" class="hv2-disc-body">
          <p>
            Tout est agrégé sur le <b>code APV à 13 chiffres</b>, jamais sur un identifiant de
            produit : c'est ce qui absorbe les parcelles portant plusieurs produits. La liste des
            APV est l'union de ce que XYLO porte en produit et de ce que SUMAL porte en dépôt —
            un APV connu du seul SUMAL apparaît donc, au lieu d'être invisible.
          </p>
          <ul>
            <li v-for="(c, i) in caveats" :key="'cav' + i">{{ c }}</li>
            <li v-for="d in devList" :key="d.key">
              <b>{{ d.label }}</b> — {{ d.state }} · fausse : {{ d.affects }}
            </li>
            <li v-if="settings.thresholds">
              Seuils appliqués : chargement <b>{{ settings.thresholds.min_m3_per_run }} m³/course</b>
              (à partir de {{ settings.thresholds.min_runs_for_loading }} courses),
              taux de sortie <b>{{ pct(settings.thresholds.extraction_alert) }}</b>,
              clôture automatique après <b>{{ settings.thresholds.no_aviz_days_to_close }} jours</b>
              sans aviz sur un permis échu.
            </li>
            <li v-if="settings.fx">
              Taux de change utilisé : <b>{{ num(settings.fx.eur_ron, 4) }}</b> RON/EUR
              ({{ settings.fx.source === 'ecb' ? `BCE du ${date(settings.fx.date)}` : 'valeur de repli' }}).
            </li>
          </ul>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import axios from '@/axios.js'
import VueApexCharts from 'vue-apexcharts'
import formatMixin from './formatMixin'
import { DEV_LIST } from './devStatus'
import HvSection from './components/HvSection'
import HvStat from './components/HvStat'
import HvPill from './components/HvPill'
import HvEmpty from './components/HvEmpty'
import HvTable from './components/HvTable'
import HvEncodeButton from './components/HvEncodeButton'
import HvApvCell from './components/HvApvCell'
import HvDepotCell from './components/HvDepotCell'
import HvGapCell from './components/HvGapCell'
import HvDensityCell from './components/HvDensityCell'
import HvDueCell from './components/HvDueCell'

// Constantes de palette au niveau module, comme QuotationBacklogAnalysis :
// une couleur définie dans un computed se recrée à chaque rendu.
const SAGE = '#95bea6'
const GRID = '#e6e6e3'
const MUTED = '#898781'
const DANGER = '#ea5455'
const WARN = '#ff9f43'

// Les opérations SUMAL qui corrigent le volume d'un APV (OP_TYPE_RO du mail).
const OP_TYPE = {
  9: 'Correction d\'APV',
  12: 'Préjudice (bois en plus de l\'APV)',
  13: 'Reprimire (correction de mesure)',
  14: 'Reprimire (non exploité)',
  15: 'Correction de stock'
}
// Repli si le serveur n'envoie pas ses règles (même valeurs par défaut).
const RULES = { done_residual_pct: 2, min_m3: 0.05, density_min: 550, density_max: 1550, adjust_days: 30 }

export default {
  mixins: [formatMixin],
  components: {
    VueApexCharts, HvSection, HvStat, HvPill, HvEmpty, HvTable,
    HvEncodeButton, HvApvCell, HvDepotCell, HvGapCell, HvDensityCell, HvDueCell
  },
  data () {
    return {
      loading: true,
      error: '',
      parcels: [],
      alerts: {},
      health: {},
      counters: {},
      caveats: [],
      settings: {},
      pace: { weeks: 26 },
      paceSeries: [],
      paceAvg: 0,
      devList: DEV_LIST,
      horizon: null,
      daily: null,
      showDisc: false
    }
  },
  computed: {
    // La densité vit DANS la tuile de son métier, comme dans le mail : une
    // tuile « Densité » seule ne pouvait en porter qu'une des deux et laissait
    // croire que la densité des achats était celle des parcelles.
    harvestingSub () {
      const d = this.daily
      if (!d) return ''
      const base = `${this.int(d.harvesting.avize)} aviz de parcelle · ${this.int(d.harvesting.trucks.total)} camion(s)`
      return `${base} · ${this.densiteTexte(d.harvesting.density, d.harvesting.weighed, d.harvesting.avize)}`
    },
    avizeSub () {
      const w = this.daily && this.daily.window
      if (!w) return ''
      const periode = w.from === w.to ? `le ${this.date(w.to)}` : `du ${this.date(w.from)} au ${this.date(w.to)}`
      const w8 = this.daily.weighings || {}
      const pont = w8.total
        ? `Pont-bascule : ${this.int(w8.total)} pesée(s), ${this.int(w8.linked_apv)} sur un aviz de parcelle, ` +
          `${this.int(w8.unlinked)} non rapprochée(s). `
        : 'Pont-bascule : aucune pesée ce jour-là. '
      return `Avize émis ${periode} : les mêmes lignes que le mail du matin. ${pont}` +
        'Plaque : le camion déclaré sur l\'aviz. Tonnage : celui de l\'aviz, réparti au prorata des m³ ' +
        'quand une pesée couvre plusieurs avize. Densité : seulement sur les avize reliés à une pesée.'
    },
    achatSub () {
      const d = this.daily
      if (!d) return ''
      const base = `${this.int(d.achat.n)} aviz de réception · ${this.int(d.achat.camions)} camion(s)`
      const absent = d.achat.absent ? ` · dont ${this.int(d.achat.absent)} absent(s) de l'ERP` : ''
      return `${base}${absent} · ${this.densiteTexte(d.achat.density, d.achat.weighed, d.achat.n)}`
    },
    parcelByApv () {
      return new Map((this.parcels || []).map((p) => [p.apv_code, p]))
    },
    rules () { return Object.assign({}, RULES, (this.daily && this.daily.rules) || {}) },
    ocols () { return (this.daily && this.daily.ocols) || {} },

    /* ----------------------------------------------- Urgences (section 1) */
    urgent () { return (this.daily && this.daily.urgent && this.daily.urgent.rows) || [] },
    urgentHidden () { return (this.daily && this.daily.urgent && this.daily.urgent.hidden) || 0 },
    urgentAlerting () { return this.urgent.filter((r) => r.alert).length },
    // Sans les lignes du serveur, « Rien d'urgent » serait un mensonge.
    urgentError () {
      if (this.loading || this.error || (this.daily && this.daily.urgent)) return ''
      return 'Les urgences n\'ont pas pu être lues. Le mail du matin les porte toujours.'
    },
    // Le détail des motifs, comme la tuile « Urgențe » du mail : combien
    // manquent de TEMPS, combien vont trop LENTEMENT.
    urgentBreakdown () {
      const rows = this.urgent
      if (!rows.length) return 'rien à signaler'
      const expired = rows.filter((r) => r.expiredGap).length
      const pace = rows.filter((r) => r.pace && !r.expiredGap).length
      const deadline = rows.length - expired - pace
      return [
        deadline ? `${deadline} échéance${deadline > 1 ? 's' : ''}` : '',
        expired ? `${expired} autorisation${expired > 1 ? 's' : ''} expirée${expired > 1 ? 's' : ''}` : '',
        pace ? `${pace} rythme trop lent` : ''
      ].filter(Boolean).join(' · ')
    },
    lowLoading () { return (this.alerts && this.alerts.low_loading) || [] },
    URGENT_SUB () {
      return 'Tous les APV ouverts — ni repris par l\'ocol, ni clos — dans l\'ordre de l\'échéance, la plus proche en tête. ' +
        'Prolongé par l\'ocol : l\'échéance comptée est le démantèlement du dépôt. ' +
        '« Stock restant » : reste en forêt selon XYLO (contrat − sorti) ou, sans contrat, stock que SUMAL déclare encore au dépôt. ' +
        '« APV − sorti − stock » : acheté sur l\'APV, moins ce qui est sorti sur les avize XYLO, moins ce que SUMAL déclare ' +
        'au dépôt ; ce qui reste manque dans XYLO (aviz non importé, bois en forêt ou cubage), à vérifier dans SUMAL.'
    },

    /* ------------------------------------------ Corrections SUMAL (mail) */
    ADJUST_SUB () {
      return 'Opérations du registre SUMAL qui changent le volume que rendra un APV, sans aviz : à la reprimire, l\'ocol ' +
        'retire ce que l\'APV promettait et que la forêt n\'a pas donné ; le préjudice est du bois en plus de l\'APV ' +
        '(arbres abîmés, à l\'ouverture d\'un chemin par exemple), mis au dépôt et sorti sur avize ; une correction d\'APV ' +
        'ou de stock garde son signe. Le volume total sous chaque APV en tient déjà compte. Le pourcentage est rapporté au contrat.'
    },

    /* --------------------------------------------- APV à encoder (section 3) */
    // Tout APV sans réception d'achat, dans l'ordre du mail : le bois déjà
    // sorti d'abord (déclaré à l'État sans facture), puis l'échéance.
    toEncode () {
      const due = (r) => (r.days_to_expiry === null || r.days_to_expiry === undefined ? 9999 : r.days_to_expiry)
      return this.parcels.filter((p) => Number(p.receptions) === 0).sort((a, b) => {
        const outA = Number(a.m3_out) > 0 ? 0 : 1
        const outB = Number(b.m3_out) > 0 ? 0 : 1
        if (outA !== outB) return outA - outB
        if (due(a) !== due(b)) return due(a) - due(b)
        return (Number(b.m3_out) || 0) - (Number(a.m3_out) || 0)
      })
    },
    toEncodeOut () { return this.toEncode.filter((p) => Number(p.m3_out) > 0) },
    toEncodeOutM3 () { return this.toEncodeOut.reduce((s, p) => s + Number(p.m3_out || 0), 0) },
    toEncodeSub () {
      if (this.toEncodeOut.length) return `${this.toEncodeOut.length} avec du bois déjà sorti`
      return this.toEncode.length ? 'aucun bois sorti' : 'chaque APV a sa réception d\'achat'
    },
    ENCODE_SUB () {
      return 'Tout APV sans réception d\'achat : connu du seul SUMAL, ou avec un produit mais sans facture. Tant qu\'elle ' +
        'manque : ni prix, ni garantie, ni échéancier. « Encode » ouvre la page où l\'on charge le contrat : Claude le lit ' +
        'et prépare la réception, qu\'on vérifie avant de la créer — le même bouton que dans le mail.'
    },

    /* ------------------------------------------------- La semaine (section 4) */
    week () { return (this.daily && this.daily.week && this.daily.week.summary) || {} },
    weekSub () {
      const w = this.daily && this.daily.week && this.daily.week.window
      if (!w) return ''
      if (w.from === w.to) return `Lundi ${this.date(w.from)}.`
      const jour = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', timeZone: 'UTC' })
        .format(new Date(`${w.to}T12:00:00Z`))
      return `Du lundi ${this.date(w.from)} au ${jour} ${this.date(w.to)}.`
    },

    /* ------------------------------------------------------ Les deux synchros */
    // Mêmes états que le panneau du mail (syncState) : fiches dépôt SUMAL,
    // trois passages par jour ; avize, lus d'après ce qui a été écrit.
    syncX2 () {
      const sync = this.daily && this.daily.sync
      if (!sync) return { tone: '', label: this.sumalSyncedLabel, detail: '' }
      const run = sync.run
      if (!run) return { tone: 'danger', label: 'aucune synchro', detail: 'aucun passage dans le journal' }
      if (run.ok === false) {
        return {
          tone: 'danger',
          label: 'échec',
          detail: `le ${this.dateTime(run.started_at)}${run.error ? ` : ${String(run.error).slice(0, 140)}` : ''}`
        }
      }
      if (run.late) return { tone: 'warn', label: 'en retard', detail: `dernier passage le ${this.dateTime(run.started_at)}` }
      return {
        tone: '',
        label: `à jour (${this.dateTime(run.started_at)})`,
        detail: `${this.int(run.depots_received)} dépôt(s) lu(s) · ${this.int(run.pending)} APV sans produit XYLO`
      }
    },
    syncX1 () {
      const h = this.health || {}
      const n24 = this.daily && this.daily.sync ? this.daily.sync.imported24h : null
      if (h.sync_dead) {
        return { tone: 'danger', label: `muette depuis ${h.silent_days} j`, detail: `${this.int(h.active_sites)} chantier(s) actif(s)` }
      }
      if (!h.last_import_at) return { tone: 'warn', label: 'inconnue', detail: 'aucun aviz importé' }
      const hours = (Date.now() - new Date(h.last_import_at).getTime()) / 3600000
      if (hours > 36) {
        return { tone: 'warn', label: `rien depuis le ${this.dateTime(h.last_import_at)}`, detail: 'normal après un jour sans aviz' }
      }
      return {
        tone: '',
        label: `à jour (${this.dateTime(h.last_import_at)})`,
        detail: n24 === null || n24 === undefined ? '' : `${this.int(n24)} aviz en 24 h`
      }
    },

    kpi () {
      const live = this.parcels.filter(
        (p) => p.status !== 'done' && p.sumal_depot_status === 'ACTIVE' && !p.permit_is_permanent
      )
      const year = new Date(); year.setFullYear(year.getFullYear() - 1)
      const recent = this.parcels.filter((p) => p.last_aviz && new Date(p.last_aviz) >= year)
      const rated = this.parcels.filter((p) => p.extraction_rate !== null && p.status !== 'to_exploit')
      return {
        remaining: live.reduce((s, p) => s + (p.remaining_m3 || 0), 0),
        liveParcels: live.length,
        out12m: recent.reduce((s, p) => s + (p.m3_out || 0), 0),
        avize12m: recent.reduce((s, p) => s + (p.avize || 0), 0),
        activeParcels: this.parcels.filter((p) => p.status === 'ongoing').length,
        withReception: this.parcels.filter((p) => p.receptions > 0).length,
        avizeTotal: this.parcels.reduce((s, p) => s + (p.avize || 0), 0),
        extractionAvg: rated.length ? rated.reduce((s, p) => s + p.extraction_rate, 0) / rated.length : null,
        expiring30: this.parcels.filter(
          (p) => p.status !== 'done' && p.days_to_expiry !== null &&
                 p.days_to_expiry <= 30 && !p.permit_is_permanent
        ).length,
        sumalStock: this.parcels.reduce((s, p) => s + (Number(p.sumal_stock_m3) || 0), 0)
      }
    },

    sumalSyncedLabel () {
      const stamps = this.parcels.map((p) => p.sumal_synced_at).filter(Boolean).sort()
      return stamps.length ? this.dateTime(stamps[stamps.length - 1]) : 'jamais'
    },


    paceChart () {
      const cats = this.paceSeries.map((p) => this.date(p.week))
      return {
        series: [{ name: 'm³ sortis', data: this.paceSeries.map((p) => Math.round(p.m3 * 10) / 10 || 0) }],
        options: {
          chart: { toolbar: { show: false }, fontFamily: 'inherit', zoom: { enabled: false } },
          colors: [SAGE],
          stroke: { width: 2, curve: 'smooth' },
          fill: { type: 'gradient', gradient: { opacityFrom: 0.28, opacityTo: 0.02, shadeIntensity: 1, stops: [0, 90, 100] } },
          dataLabels: { enabled: false },
          xaxis: {
            categories: cats,
            tickAmount: 8,
            axisBorder: { color: GRID }, axisTicks: { color: GRID },
            labels: { style: { colors: MUTED, fontSize: '11px' } }
          },
          yaxis: { min: 0, labels: { style: { colors: MUTED }, formatter: (v) => this.m3int(v) } },
          grid: { borderColor: GRID, strokeDashArray: 0 },
          tooltip: { y: { formatter: (v) => `${this.m3(v)} m³` } },
          annotations: {
            yaxis: [{
              y: this.paceAvg, borderColor: MUTED, strokeDashArray: 4,
              label: {
                text: `moyenne ${this.m3int(this.paceAvg)} m³/semaine`,
                position: 'left', textAnchor: 'start',
                style: { color: MUTED, background: 'transparent', fontSize: '11px' }
              }
            }]
          }
        }
      }
    },

    remainingChart () {
      const rows = this.parcels
        .filter((p) => p.status !== 'done' && (p.remaining_m3 || 0) > 0)
        .sort((a, b) => (b.remaining_m3 || 0) - (a.remaining_m3 || 0))
        .slice(0, 12)
      return {
        series: [{ name: 'm³ restants', data: rows.map((p) => Math.round(p.remaining_m3)) }],
        options: {
          chart: { toolbar: { show: false }, fontFamily: 'inherit' },
          plotOptions: { bar: { horizontal: true, barHeight: '62%', borderRadius: 3, distributed: true } },
          colors: rows.map((p) => {
            const d = p.days_to_expiry
            if (d === null || p.permit_is_permanent) return MUTED
            if (d <= 15) return DANGER
            if (d <= 30) return WARN
            return SAGE
          }),
          legend: { show: false },
          dataLabels: { enabled: false },
          xaxis: {
            categories: rows.map((p) => this.parcelName(p)),
            axisBorder: { color: GRID }, axisTicks: { color: GRID },
            labels: { style: { colors: MUTED, fontSize: '11px' }, formatter: (v) => this.m3int(v) }
          },
          yaxis: { labels: { style: { colors: MUTED, fontSize: '11px' }, maxWidth: 160 } },
          grid: { borderColor: GRID },
          tooltip: {
            y: {
              formatter: (v, ctx) => {
                const p = rows[ctx.dataPointIndex]
                const d = p && p.days_to_expiry
                return `${this.m3(v)} m³ — ${d === null ? 'sans échéance' : (d < 0 ? `expiré depuis ${-d} j` : `${d} j restants`)}`
              }
            }
          }
        }
      }
    }
  },
  methods: {
    parcelOf (r) { return this.parcelByApv.get(r.apv) || null },
    // Le motif principal d'une ligne d'Urgences, règle du mail (blocUrgences) :
    // un APV seulement trop lent n'a que sa pastille de rythme.
    motif (r) {
      if (r.pace && r.encoded && !r.extended) return null
      if (!r.encoded) return { label: 'APV non encodé', tone: 'danger' }
      if (r.extended) return { label: `Prolongé jusqu'au ${this.date(r.deadline)}`, tone: 'ok' }
      if (r.fromStock) {
        return { label: r.days !== null && r.days < 0 ? 'Stock au dépôt expiré' : 'Stock au dépôt', tone: 'info' }
      }
      return { label: 'Échéance', tone: '' }
    },
    // Sans réception d'achat, le bouton « Encode » du mail (Chady, 2026-10-27).
    needsEncode (r) {
      const p = this.parcelOf(r)
      return !r.encoded || (!!p && Number(p.receptions) === 0)
    },
    // Sous un cumul de l'année, le total par essence, comme sous l'en-tête du
    // mail (sortimentLine) : chaque essence = somme de ses deux usages arrondis.
    splitText (split) {
      if (!split) return ''
      const ess = (k) => Math.round(Number(split[k] && split[k].rotund) || 0) + Math.round(Number(split[k] && split[k].foc) || 0)
      const out = [`Salcâm ${this.int(ess('salcam'))}`, `Diverse ${this.int(ess('diverse'))}`]
      const rest = []
      if (Number(split.autre) >= 0.5) rest.push(`autres produits ${this.int(split.autre)}`)
      if (Number(split.nonVentile) >= 0.5) rest.push(`sans sortiment ${this.int(split.nonVentile)}`)
      if (rest.length) out.push(`non réparti : ${rest.join(', ')}`)
      return out.join(' · ')
    },
    depotLabel (r) { return String(r.depot_name || '').replace(/^\d{13}\s*-\s*/, '') },
    opLabel (r) { return OP_TYPE[Number(r.op_type_code)] || r.op_type || this.DASH },
    cancelled (r) { return !!r.status && r.status !== 'INREGISTRAT' },
    signed (v) {
      const n = Number(v) || 0
      return `${n < 0 ? '−' : '+'}${this.m3(Math.abs(n))}`
    },
    // La part de l'APV qu'une correction représente, rapportée au CONTRAT : c'est
    // lui que la correction révise (Chady, 2026-10-15).
    adjustShare (r) {
      const p = this.parcelByApv.get(r.apv_code)
      const contract = p && (Number(p.cubage_contract_m3) || (p.volume && Number(p.volume.contract)) || 0)
      if (!contract || !Number(r.volume_m3)) return null
      const pct = (Math.abs(Number(r.volume_m3)) / contract) * 100
      return { pct: `${this.num(pct, pct < 1 ? 1 : 0)} %`, contract }
    },
    /** Le nom de la parcelle quand le tableau de bord la suit, sinon celui du produit. */
    apvName (r) {
      return this.parcelName(this.parcelByApv.get(r.apv_code) || r)
    },
    /**
     * Une densité vide a deux causes opposées — rien n'est sorti, ou rien n'a
     * été pesé — et la tuile doit dire laquelle, sinon « — » se lit en panne.
     */
    densiteTexte (density, weighed, total) {
      if (density === null || density === undefined) {
        return total ? 'densité — (aucune pesée rapprochée)' : 'densité — (aucun aviz)'
      }
      return `densité ${this.int(density)} kg/m³ (${this.int(weighed)} pesé(s) sur ${this.int(total)})`
    },

    async load () {
      this.loading = true
      this.error = ''
      try {
        // UNE requête pour le tableau de bord : /overview n'exécute le ROLLUP
        // qu'une fois, là où /parcels + /alerts + /health le lançaient trois fois
        // (417 ms l'exécution) et transmettaient les parcelles deux fois.
        const [overview, pace, settings] = await Promise.all([
          axios.get('/harvesting2/overview'),
          axios.get('/harvesting2/pace?weeks=26').catch(() => ({ data: null })),
          axios.get('/harvesting2/settings').catch(() => ({ data: {} }))
        ])
        this.parcels = overview.data.parcels || []
        this.horizon = overview.data.horizon || null
        this.daily = overview.data.daily || null
        this.alerts = overview.data.alerts || {}
        this.health = overview.data.health || {}
        this.counters = this.alerts.counters || {}
        this.caveats = this.alerts.caveats || []
        this.settings = settings.data || {}
        if (pace.data) {
          this.pace = pace.data
          this.paceSeries = pace.data.series || []
          this.paceAvg = pace.data.avg_m3_per_week || 0
        }
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message || 'Erreur inconnue'
      } finally {
        this.loading = false
      }
    }
  },
  created () { this.load() }
}
</script>
